import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { motion } from 'framer-motion';
import RightColumnNav from './RightColumnNav';
import type { NavItem } from './NavLinks';
import { isOverlayRoute, normalizePathname, stripHashAndQuery } from '@/lib/navigationRoutes';

const NAV_ITEMS: NavItem[] = [
  { label: 'About', href: '/about', overlay: '/about' },
  { label: 'Rapid SaaS', href: '/services', overlay: '/services' },
  { label: 'AI Agents', href: '/services/nexus', overlay: '/services/nexus' },
  { label: 'Work', href: '/work', overlay: '/work' },
  { label: 'Resources', href: '/blog', overlay: '/blog' },
];

const NORMALIZED_NAV_TARGETS = NAV_ITEMS.map((item) =>
  normalizePathname(stripHashAndQuery(item.overlay || item.href))
);

type NavigationState = {
  backgroundLocation?: Location;
};

export const RightColumnSiteNav = RightColumnNav;

const SiteNav: FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as NavigationState | undefined) ?? undefined;
  const backgroundLocation = (state?.backgroundLocation as Location | undefined) ?? location;
  const normalizedCurrentPath = normalizePathname(location.pathname ?? '/');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavigate = (href: string, overlay?: string) => {
    if (href === '/contact') {
      if (typeof window !== 'undefined') {
        // @ts-expect-error custom drawer event dispatched globally
        window.dispatchEvent(new Event('open-contact'));
      } else {
        navigate('/contact');
      }
      return;
    }

    const target = overlay || href;
    const targetPath = stripHashAndQuery(target);

    if (isOverlayRoute(targetPath)) {
      navigate(target, {
        state: { backgroundLocation },
      });
      return;
    }

    navigate(href);
  };

  const isActive = (path: string) => {
    const target = normalizePathname(stripHashAndQuery(path));
    if (!target || target === '/') return normalizedCurrentPath === '/';
    if (normalizedCurrentPath === target) return true;

    if (normalizedCurrentPath.startsWith(`${target}/`)) {
      const hasMoreSpecificMatch = NORMALIZED_NAV_TARGETS.some(
        (other) =>
          other !== target &&
          (normalizedCurrentPath === other || normalizedCurrentPath.startsWith(`${other}/`))
      );
      return !hasMoreSpecificMatch;
    }

    return false;
  };

  const brandPath = normalizePathname(stripHashAndQuery(backgroundLocation.pathname ?? '/'));
  const brandLabel = brandPath === '/' ? 'welcome' : 'turbo labs, inc.';

  return (
    <div className="pointer-events-none fixed left-0 right-0 top-0 z-[220]">
      <div className="pointer-events-auto">
        <div className="relative mx-auto w-full max-w-[2200px] px-4 py-2">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: scrolled ? 1 : 0, y: scrolled ? 0 : -8 }}
            transition={{ duration: 0.25 }}
            className="pointer-events-none absolute inset-0 rounded-[12px] bg-white/55 ring-1 ring-black/5 shadow-[0_8px_30px_-20px_rgba(0,0,0,0.45)] backdrop-blur-xl backdrop-saturate-150 dark:bg-zinc-900/35 dark:ring-white/10 md:rounded-[14px]"
          />

          <div className="relative z-10 grid w-full grid-cols-1 items-center gap-4 md:grid-cols-[260px_minmax(0,1fr)] md:gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
            <button
              type="button"
              onClick={() => handleNavigate('/')}
              className="hidden border-0 bg-transparent p-0 text-left text-sm font-medium text-zinc-700 outline-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:text-zinc-200 dark:focus-visible:ring-offset-zinc-900 md:block"
              aria-label="Go to home page"
            >
              {brandLabel}
            </button>
            <div className="flex w-full justify-end">
              <div className="w-full max-w-[1920px] px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20">
                <RightColumnNav items={NAV_ITEMS} isActive={isActive} onNavigate={handleNavigate} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteNav;
